import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  ArrowLeft, 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  User,
  FileText,
  Upload,
  Download,
  Trash2,
  MessageSquare,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Edit,
  Save,
  X,
  History,
  Rocket
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAD_STATUSES = [
  { value: 'r1_effectue', label: 'R1 effectue', color: 'bg-blue-100 text-blue-700' },
  { value: 'r2_effectue', label: 'R2 effectue', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'devis_envoye', label: 'Devis envoye', color: 'bg-purple-100 text-purple-700' },
  { value: 'contrat_envoye', label: 'Contrat envoye', color: 'bg-orange-100 text-orange-700' },
  { value: 'facture_envoyee', label: 'Facture envoyee', color: 'bg-teal-100 text-teal-700' },
];

const DOCUMENT_TYPES = [
  { value: 'devis', label: 'Devis' },
  { value: 'devis_signe', label: 'Devis signe' },
  { value: 'accord_cadre', label: 'Accord cadre' },
  { value: 'accord_cadre_signe', label: 'Accord cadre signe' },
  { value: 'contrat', label: 'Contrat' },
  { value: 'contrat_signe', label: 'Contrat signe' },
  { value: 'facture', label: 'Facture' },
  { value: 'autre', label: 'Autre' },
];

const ACADEMIES = [
  'Aix-Marseille', 'Amiens', 'Besancon', 'Bordeaux', 'Caen',
  'Clermont-Ferrand', 'Corse', 'Creteil', 'Dijon', 'Grenoble',
  'Guadeloupe', 'Guyane', 'Lille', 'Limoges', 'Lyon',
  'Martinique', 'Mayotte', 'Montpellier', 'Nancy-Metz', 'Nantes',
  'Nice', 'Normandie', 'Orleans-Tours', 'Paris', 'Poitiers',
  'Reims', 'Rennes', 'Reunion', 'Strasbourg', 'Toulouse', 'Versailles'
];

export const LeadProfile = () => {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState('info');
  
  // Document upload
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({ document_type: '', notes: '' });
  const fileInputRef = useRef(null);
  
  // Comment
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  
  // Convert
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    fetchProspect();
  }, [prospectId]);

  const fetchProspect = async () => {
    try {
      const response = await fetch(`${API}/prospects/${prospectId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setProspect(data);
        setEditData(data);
      } else {
        toast.error('Prospect non trouve');
        navigate('/leads-hot');
      }
    } catch (error) {
      console.error('Failed to fetch prospect:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/prospects/${prospectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editData),
      });
      
      if (response.ok) {
        toast.success('Modifications enregistrees');
        setIsEditing(false);
        fetchProspect();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      const response = await fetch(`${API}/prospects/${prospectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (response.ok) {
        toast.success('Statut mis a jour');
        fetchProspect();
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Erreur');
    }
  };

  const updateCheckbox = async (field, value) => {
    try {
      const response = await fetch(`${API}/prospects/${prospectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value }),
      });
      
      if (response.ok) {
        toast.success('Mise a jour effectuee');
        fetchProspect();
      }
    } catch (error) {
      console.error('Checkbox update error:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas depasser 10 Mo');
      return;
    }

    if (!uploadData.document_type) {
      toast.error('Selectionnez un type de document');
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result.split(',')[1];
        
        const response = await fetch(`${API}/prospects/${prospectId}/documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            document_type: uploadData.document_type,
            file_name: file.name,
            file_data: base64,
            file_type: file.name.split('.').pop().toLowerCase(),
            notes: uploadData.notes
          }),
        });

        if (response.ok) {
          toast.success('Document uploade');
          setIsUploadOpen(false);
          setUploadData({ document_type: '', notes: '' });
          fetchProspect();
        } else {
          toast.error('Erreur lors de l\'upload');
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Erreur de connexion');
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await fetch(
        `${API}/prospects/${prospectId}/documents/${doc.document_id}/download`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.file_name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur de telechargement');
    }
  };

  const deleteDocument = async (docId) => {
    if (!confirm('Supprimer ce document ?')) return;
    
    try {
      const response = await fetch(
        `${API}/prospects/${prospectId}/documents/${docId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      
      if (response.ok) {
        toast.success('Document supprime');
        fetchProspect();
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    
    setSendingComment(true);
    try {
      const response = await fetch(`${API}/prospects/${prospectId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newComment }),
      });
      
      if (response.ok) {
        setNewComment('');
        fetchProspect();
      }
    } catch (error) {
      console.error('Comment error:', error);
    } finally {
      setSendingComment(false);
    }
  };

  const convertToSale = async () => {
    if (!prospect.devis_signe || !prospect.contrat_signe) {
      toast.error('Cochez les 3 cases (Devis, Accord cadre, Contrat signes) pour convertir');
      return;
    }
    
    setConverting(true);
    try {
      const response = await fetch(`${API}/prospects/${prospectId}/convert`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Redirection vers le formulaire de vente...');
        // Redirect to sale form with prospect_id for pre-filling
        navigate(`/declare-sale?prospect_id=${prospectId}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de conversion');
      }
    } catch (error) {
      console.error('Convert error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setConverting(false);
    }
  };

  const canConvert = prospect?.devis_signe && prospect?.contrat_signe && !prospect?.converted_sale_id;

  const getStatusConfig = (status) => {
    return LEAD_STATUSES.find(s => s.value === status) || LEAD_STATUSES[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!prospect) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/leads-hot')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-[Manrope]">
              {prospect.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <Badge className={getStatusConfig(prospect.status).color}>
                {getStatusConfig(prospect.status).label}
              </Badge>
              {prospect.academy && (
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {prospect.academy}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                <X className="w-4 h-4 mr-2" /> Annuler
              </Button>
              <Button onClick={saveChanges} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              <Edit className="w-4 h-4 mr-2" /> Modifier
            </Button>
          )}
        </div>
      </div>

      {/* Conversion CTA */}
      {canConvert && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-emerald-100">
                <Rocket className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-emerald-800">Lead qualifie !</p>
                <p className="text-sm text-emerald-600">
                  Tous les documents sont signes. Completez le formulaire pour finaliser la vente.
                </p>
              </div>
            </div>
            <Button 
              onClick={convertToSale} 
              disabled={converting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
              Convertir en vente
            </Button>
          </CardContent>
        </Card>
      )}
      
      {prospect.converted_sale_id && (
        <Card className="border-indigo-200 bg-indigo-50">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-indigo-100">
                <CheckCircle className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="font-medium text-indigo-800">Lead deja converti</p>
                <p className="text-sm text-indigo-600">
                  Ce lead a ete converti en vente ({prospect.converted_sale_id})
                </p>
              </div>
            </div>
            <Button 
              variant="outline"
              onClick={() => navigate('/my-sales')}
            >
              Voir mes ventes
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="info">Informations</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="comments">Commentaires</TabsTrigger>
              <TabsTrigger value="history">Historique</TabsTrigger>
            </TabsList>

            {/* Info Tab */}
            <TabsContent value="info" className="space-y-4 mt-4">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Etablissement</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nom</Label>
                          <Input
                            value={editData.name || ''}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Academie</Label>
                          <Select
                            value={editData.academy || ''}
                            onValueChange={(v) => setEditData({ ...editData, academy: v })}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ACADEMIES.map(a => (
                                <SelectItem key={a} value={a}>{a}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Input
                            value={editData.establishment_type || ''}
                            onChange={(e) => setEditData({ ...editData, establishment_type: e.target.value })}
                            placeholder="College, Lycee..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Adresse</Label>
                          <Input
                            value={editData.address_street || ''}
                            onChange={(e) => setEditData({ ...editData, address_street: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Ville</Label>
                          <Input
                            value={editData.address_city || ''}
                            onChange={(e) => setEditData({ ...editData, address_city: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Code postal</Label>
                          <Input
                            value={editData.address_postal || ''}
                            onChange={(e) => setEditData({ ...editData, address_postal: e.target.value })}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Nom</p>
                        <p className="font-medium">{prospect.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Academie</p>
                        <p className="font-medium">{prospect.academy || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Type</p>
                        <p className="font-medium">{prospect.establishment_type || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Adresse</p>
                        <p className="font-medium">
                          {prospect.address_street || '-'}
                          {prospect.address_city && `, ${prospect.address_city}`}
                          {prospect.address_postal && ` ${prospect.address_postal}`}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nom du contact</Label>
                        <Input
                          value={editData.contact_name || ''}
                          onChange={(e) => setEditData({ ...editData, contact_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Fonction</Label>
                        <Input
                          value={editData.contact_function || ''}
                          onChange={(e) => setEditData({ ...editData, contact_function: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={editData.contact_email || ''}
                          onChange={(e) => setEditData({ ...editData, contact_email: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Telephone</Label>
                        <Input
                          value={editData.contact_phone || ''}
                          onChange={(e) => setEditData({ ...editData, contact_phone: e.target.value })}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Nom</p>
                        <p className="font-medium">{prospect.contact_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Fonction</p>
                        <p className="font-medium">{prospect.contact_function || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Email</p>
                        {prospect.contact_email ? (
                          <a href={`mailto:${prospect.contact_email}`} className="font-medium text-indigo-600 hover:underline">
                            {prospect.contact_email}
                          </a>
                        ) : <p className="font-medium">-</p>}
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Telephone</p>
                        {prospect.contact_phone ? (
                          <a href={`tel:${prospect.contact_phone}`} className="font-medium text-indigo-600 hover:underline">
                            {prospect.contact_phone}
                          </a>
                        ) : <p className="font-medium">-</p>}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {prospect.notes && (
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 whitespace-pre-wrap">{prospect.notes}</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents" className="mt-4">
              <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Documents</CardTitle>
                  <Button onClick={() => setIsUploadOpen(true)}>
                    <Upload className="w-4 h-4 mr-2" /> Ajouter
                  </Button>
                </CardHeader>
                <CardContent>
                  {prospect.documents?.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Aucun document</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {prospect.documents?.map((doc) => (
                        <div
                          key={doc.document_id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-8 h-8 text-indigo-500" />
                            <div>
                              <p className="font-medium">{doc.file_name}</p>
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Badge variant="outline">{doc.document_type}</Badge>
                                <span>{formatDate(doc.created_at)}</span>
                                <span>par {doc.uploaded_by_name}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => downloadDocument(doc)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500"
                              onClick={() => deleteDocument(doc.document_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Comments Tab */}
            <TabsContent value="comments" className="mt-4">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Commentaires</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-3">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Ajouter un commentaire..."
                      rows={2}
                    />
                    <Button onClick={addComment} disabled={sendingComment || !newComment.trim()}>
                      {sendingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                  <Separator />
                  <div className="space-y-4">
                    {prospect.comments?.length === 0 ? (
                      <p className="text-center text-slate-500 py-4">Aucun commentaire</p>
                    ) : (
                      prospect.comments?.map((comment) => (
                        <div key={comment.comment_id} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-sm">{comment.user_name}</span>
                            <span className="text-xs text-slate-400">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="text-slate-600">{comment.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="mt-4">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Historique des statuts</CardTitle>
                </CardHeader>
                <CardContent>
                  {prospect.status_history?.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">Aucun historique</p>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                      <div className="space-y-4">
                        {prospect.status_history?.map((entry, index) => (
                          <div key={entry.history_id} className="relative pl-10">
                            <div className={`absolute left-2 w-5 h-5 rounded-full flex items-center justify-center ${
                              index === 0 ? 'bg-indigo-100' : 'bg-slate-100'
                            }`}>
                              <div className={`w-2 h-2 rounded-full ${
                                index === 0 ? 'bg-indigo-600' : 'bg-slate-400'
                              }`} />
                            </div>
                            <div className="p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <History className="w-4 h-4 text-slate-400" />
                                <span className="font-medium text-sm capitalize">
                                  {entry.action.replace(/_/g, ' ')}
                                </span>
                              </div>
                              {entry.old_value && (
                                <p className="text-sm text-slate-500">
                                  {entry.old_value} → {entry.new_value}
                                </p>
                              )}
                              {!entry.old_value && entry.new_value && (
                                <p className="text-sm text-slate-500">{entry.new_value}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                {entry.user_name} - {formatDate(entry.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Statut</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={prospect.status} onValueChange={updateStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <Badge className={s.color}>{s.label}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Checkboxes for conversion */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Documents signes</CardTitle>
              <CardDescription>
                Cochez quand les documents sont signes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="devis_signe"
                  checked={prospect.devis_signe || false}
                  onCheckedChange={(checked) => updateCheckbox('devis_signe', checked)}
                />
                <Label htmlFor="devis_signe" className="flex items-center gap-2">
                  {prospect.devis_signe ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-slate-300" />
                  )}
                  Devis signe
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="contrat_signe"
                  checked={prospect.contrat_signe || false}
                  onCheckedChange={(checked) => updateCheckbox('contrat_signe', checked)}
                />
                <Label htmlFor="contrat_signe" className="flex items-center gap-2">
                  {prospect.contrat_signe ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-slate-300" />
                  )}
                  Contrat signe
                </Label>
              </div>
              
              {canConvert && (
                <div className="pt-4">
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={convertToSale}
                    disabled={converting}
                  >
                    {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                    Convertir en vente
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Meta info */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6 text-sm text-slate-500 space-y-2">
              <div className="flex items-center justify-between">
                <span>Cree le</span>
                <span>{formatDate(prospect.created_at)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Modifie le</span>
                <span>{formatDate(prospect.updated_at)}</span>
              </div>
              {prospect.assigned_user && (
                <div className="flex items-center justify-between">
                  <span>Assigne a</span>
                  <span>{prospect.assigned_user.name}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un document</DialogTitle>
            <DialogDescription>
              Uploadez un document pour ce lead
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type de document *</Label>
              <Select
                value={uploadData.document_type}
                onValueChange={(v) => setUploadData({ ...uploadData, document_type: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selectionnez un type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Input
                value={uploadData.notes}
                onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
                placeholder="Notes sur ce document..."
              />
            </div>
            <div className="space-y-2">
              <Label>Fichier *</Label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !uploadData.document_type}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Choisir un fichier
              </Button>
              <p className="text-xs text-slate-400">PDF, DOC, PNG, JPG (max 10 Mo)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadOpen(false)}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
